/**
 * This file holds all the global constants that are used throughout
 * the client application
 */

//  API ROOT
export const SOCKET_RETRY_ATTEMPTS = import.meta.env.PROD ? Infinity : 100;
export const API_URL = import.meta.env.VITE_API_URL;
// reCAPTCHA v3 site key (public); the secret lives on the server.
export const RECAPTCHA_SITE_KEY = "6LcDYE8aAAAAADaMekitwzMPBa94hv4oTfizNN5k";

/**
 * Countries hidden from every phone picker on a signup / OTP surface.
 * Mirrors the server's UNSUPPORTED_PHONE_COUNTRIES (which rejects them) and
 * the AWS SMS block list — keep the three in sync. iso2, lowercase, as
 * react-phone-input-2's `excludeCountries` expects.
 */
export const UNSUPPORTED_PHONE_COUNTRIES = [
  "af",
  "am",
  "az",
  "by",
  "cd",
  "iq",
  "ke",
  "kg",
  "kz",
  "ly",
  "ma",
  "md",
  "mg",
  "mm",
  "na",
  "ph",
  "ru",
  "sn",
  "tj",
  "tm",
  "tz",
  "ua",
  "ug",
  "uz",
  "ye",
  "zw",
];
export const ARGONAUT_CLIENT_URL = import.meta.env.VITE_ARGONAUT_CLIENT_URL;
export const ARGONAUT_API_URL = import.meta.env.VITE_ARGONAUT_API_URL;
export const CDN_URL = String(
  import.meta.env.VITE_CDN_PUBLIC_ASSET_URL
).endsWith("/")
  ? import.meta.env.VITE_CDN_PUBLIC_ASSET_URL
  : `${import.meta.env.VITE_CDN_PUBLIC_ASSET_URL}/`;

/**
 * Build a CDN URL for an S3 image path, optionally requesting a resized version.
 * Appends ?d=WxH to leverage the CloudFront Lambda@Edge image resize function.
 * Falls through gracefully: if path is already an absolute URL or sizePx is not
 * provided, returns the URL without resize params.
 *
 * @param {string} path - S3 key (e.g. "public/profile/.../avatar.png") or absolute URL
 * @param {number} [sizePx] - Desired display size in CSS pixels (largest axis).
 *                             Will be doubled for retina and capped at 3000px (CDN limit).
 * @returns {string} Full CDN URL, optionally with ?d=WxH
 */
export function buildCdnUrl(path, sizePx) {
  if (!path) return "";
  const isS3Path = path.split("/")[0] === "public";
  const base = isS3Path ? `${CDN_URL}${path}` : path;
  if (!sizePx || !isS3Path) return base;
  const size = Math.min(sizePx * 2, 3000);
  return `${base}?d=${size}x${size}`;
}
export const SOCKET_URL = import.meta.env.VITE_SOCKET_URL;
export const PROXY_URL = import.meta.env.VITE_PROXY_URL;
export const MEET_URL = import.meta.env.VITE_MEET_URL;
export const LINKEDIN_OAUTH_REDIRECT_URI = import.meta.env
  .VITE_LINKEDIN_OAUTH_REDIRECT_URI;
export const GOOGLE_OAUTH_REDIRECT_URI = import.meta.env
  .VITE_GOOGLE_OAUTH_REDIRECT_URI;
export const GOOGLE_OAUTH_CLIENT_ID = import.meta.env
  .VITE_GOOGLE_OAUTH_CLIENT_ID;

export const FORGE_ORIGIN = import.meta.env.VITE_FORGE_ORIGIN;
export const SHOULD_LOG_ACTIVITY = import.meta.env.PROD;

// GCC API
export const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

// "ASPIRANT", "ENROLLED", "ALUMNUS"
export const ASPIRANT = "ASPIRANT";
export const ENROLLED = "ENROLLED";
export const ALUMNUS = "ALUMNUS";
export const IMMIGRATION_ASPIRANT = "IMMIGRATION_ASPIRANT";
export const SERVICE_PROVIDER = "SERVICE_PROVIDER";

// "HOME", "ENROLLED", "CURRENT", "ASPIRING"
export const HOME = "HOME";
export const CURRENT = "CURRENT";
export const ASPIRING = "ASPIRING";

// Social Media Sharing Links
export const SHARE_TO_FACEBOOK = "https://facebook.com/sharer/sharer.php?u=";
export const SHARE_TO_WHATSAPP = "whatsapp://send?text=";
export const SHARE_TO_WHATSAPP_WEB = "https://web.whatsapp.com/send?text=";

// Social Media Sharing Methods
export const SHARE_METHOD_COPY = "copy";
export const SHARE_METHOD_WHATSAPP = "whatsapp";

// Responsive breakpoint widths in pixels.
// NOTE: These are in accordance with the Tailwind's default breakpoints.
export const WIDTH_SM = "640";
export const WIDTH_MD = "768";
export const WIDTH_LG = "1024";
export const WIDTH_XL = "1280";
export const WIDTH_2XL = "1536";
export const WIDTH_3XL = "1920";
export const WIDTH_4XL = "2560";

// Payment URLS
export const RAZORPAY_CLIENT_ID = import.meta.env.VITE_RAZORPAY_CLIENT_ID;

// Local Storage Key of Activity Logging Library
export const ACTIVITY_KEY = "activity";
export const USER_PROFILE_DATA_KEY = "userProfileData";
export const BROWSER_FINGER_PRINT_KEY = "browserFingerPrint";
export const LAST_ACTIVITY_SYNC = "lastActivitySync";

// Meet CONSTANTs
export const MEET_GIG_TYPE_ONE_ON_ONE = "ONE_ON_ONE";
export const MEET_GIG_TYPE_LIVE_CLASSROOM = "LIVE_CLASSROOM";
export const MEET_GIG_TYPE_LEARNING_CONTENT = "LEARNING_CONTENT";
export const MEET_GIG_TYPE_NBYULA_TALK = "NBYULA_TALK";

// Gig Constants
export const GIG_TYPE_ONE_ON_ONE = "One-on-one, Personalised gigs";
export const GIG_TYPE_LIVE_CLASSROOM = "Live Classroom Teaching";
export const GIG_TYPE_LEARNING_CONTENT = "Learning Content";
export const GIG_TYPE_NBYULA_TALK = "Nbyula Talk";
export const GIG_TYPE_NBYULA_CLASSIFIED = "Nbyula Classified";

export const GIG_TO_MEET_MAPPING = {
  [GIG_TYPE_ONE_ON_ONE]: MEET_GIG_TYPE_ONE_ON_ONE,
  [GIG_TYPE_LIVE_CLASSROOM]: MEET_GIG_TYPE_LIVE_CLASSROOM,
  [GIG_TYPE_LEARNING_CONTENT]: MEET_GIG_TYPE_LEARNING_CONTENT,
  [GIG_TYPE_NBYULA_TALK]: MEET_GIG_TYPE_NBYULA_TALK,
};

export const GIG_SUBSCRIBER_ROLE_PROVIDER = "PROVIDER";
export const GIG_SUBSCRIBER_ROLE_SEEKER = "SEEKER";
export const GIG_SUBSCRIBER_ROLE_MODERATOR = "MODERATOR";
export const GIG_SUBSCRIBER_ROLE_ADMIN = "ADMIN";

export const GIG_SUBSCRIBER_ROLE = {
  1: GIG_SUBSCRIBER_ROLE_PROVIDER,
  2: GIG_SUBSCRIBER_ROLE_SEEKER,
  3: GIG_SUBSCRIBER_ROLE_MODERATOR,
  4: GIG_SUBSCRIBER_ROLE_ADMIN,
};

export const GIG_SUBSCRIBER_ROLE_ENUM = {
  PROVIDER: 1,
  SEEKER: 2,
  MODERATOR: 3,
  ADMIN: 4,
};

export const GIG_SUBSCRIBER_STATE = {
  PILOT: 1,
  INQUIRY: 2,
  PAYMENT_COMPLETED: 3,
  COMPLETE: 4,
  INTERESTED: 5,
  INTERESTED_IN_RECORDING: 6,
};

export const NOTIFICATION_SOCKET_URL = import.meta.env
  .VITE_NOTIFICATION_SOCKET_URL;

export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
export const TRACING_ORIGIN_SENTRY = import.meta.env.VITE_TRACING_ORIGIN_SENTRY;

export const NBYULA_TITLE = "Nbyula";
export const NBYULA_TITLE_FULL = "Nbyula - Skillizens without Borders";

// Common states of Uploading a File
export const NOT_STARTED = "NOT_STARTED";
export const UPLOADING = "UPLOADING";
export const UPLOAD_SUCCESS = "UPLOAD_SUCCESS";
export const UPLOAD_ERROR = "UPLOAD_ERROR";

// quest route constants
export const INTENT_ROUTE = "intent";
export const INTENT_ROUTE_LOOKING_FOR_ANSWER = "looking-for-answer";
export const INTENT_ROUTE_REQUEST_FOR_ANSWER = "request-for-answer";
export const INTENT_ROUTE_WRITE_AN_ANSWER = "write-an-answer";
export const INTENT_ROUTE_TALK_N_EARN = "talk-n-earn";
export const INTENT_ROUTE_ANSWER_IS_HELPFUL = "answer-is-helpful";
export const INTENT_ROUTE_FOLLOW_QUEST = "follow-this-quest";
export const INTENT_ROUTE_UNFOLLOW_QUEST = "unfollow-this-quest";

export const ASK_QUESTION_PAGE = 1;
export const ADD_TOPIC_PAGE = 2;

export const ENTITY_TYPE_QUEST = 1;

export const REACT_QUILL_READ_ONLY_CLASS = "read-only-editor";
export const MEDIA_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
  APPLICATION: "application",
  ALL: "*",
};

export const BLOT_NAME_NBYULA_CLIPPER_YOUTUBE_CLIP =
  "nbyula-clipper-youtube-clip";
export const BLOT_NAME_NBYULA_CLIPPER_TEXT_CLIP = "nbyula-clipper-text-clip";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_CLIPPER_CLIP_ID =
  "data-nbyula-clipper-clip-id";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_CLIPPER_YOUTUBE_URL =
  "data-nbyula-clipper-youtube-url";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_CLIPPER_YOUTUBE_CAPTIONS =
  "data-nbyula-clipper-youtube-captions";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED = "data-link-embed";
export const BLOT_NAME_NBYULA_LINK_EMBED = "link-embed";
export const BLOT_NAME_NBYULA_ARTIFACT_EMBED = "nbyula-artifact-embed";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_ARTIFACT_EMBED =
  "data-nbyula-artifact-id";
/**
 * Matches artifact URLs like:
 *   https://artifacts.nbyula.com/artifacts/<uuid>
 *   https://artifacts.nbyula.com/artifacts/<uuid>/v1/index.html
 */
export const ARTIFACT_URL_REGEX =
  /https?:\/\/artifacts\.nbyula\.com\/artifacts\/([a-f0-9-]{36})(?:\/v\d+\/index\.html)?/;
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED_TITLE =
  "data-link-embed-title";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED_CONTENT =
  "data-link-embed-content";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED_IMAGE_SRC =
  "data-link-embed-img-src";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED_STYLE =
  "data-link-embed-style";
export const CUSTOM_DATA_ATTRIBUTE_NBYULA_LINK_EMBED_STYLES = {
  TEXT: "TEXT",
  PREVIEW: "PREVIEW",
};
export const BLOT_NAME_MEDIA_VIDEO = "media-video";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO = "data-media-video";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO_ATTR = {
  TYPE: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__type",
  NAME: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__name",
  KEY: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__key",
  THUMBNAIL: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__thumbnail",
  VISIBLE: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__visible",
  PROTECTED: CUSTOM_DATA_ATTRIBUTE_MEDIA_VIDEO + "__protected",
};

export const BLOT_NAME_MEDIA_IMAGE = "media-image";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE = "data-media-image";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE_ATTR = {
  TYPE: CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE + "__type",
  NAME: CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE + "__name",
  KEY: CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE + "__key",
  VISIBLE: CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE + "__visible",
  PROTECTED: CUSTOM_DATA_ATTRIBUTE_MEDIA_IMAGE + "__protected",
};

export const BLOT_NAME_MEDIA_FILE = "media-file";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE = "data-media-file";
export const CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE_ATTR = {
  TYPE: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__type",
  NAME: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__name",
  KEY: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__key",
  VISIBLE: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__visible",
  PROTECTED: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__protected",
  SIZE: CUSTOM_DATA_ATTRIBUTE_MEDIA_FILE + "__size",
};

export const URL_CHECK_REGEX =
  /(https?:\/\/|www\.)[\w-\.]+\.[\w-\.]+(\/([\S]+)?)?/i;

//Constants For Quest Posts
export const POST_FEED_PAGE = "POST_FEED_PAGE";
export const POST_COMMUNITY_FEED_PAGE = "POST_COMMUNITY_FEED_PAGE";

export const COMMENT_RELATION_ENTITY_TYPE = {
  QUEST: 1,
  QUEST_ANSWER: 2,
  SKY_LIFT: 3,
  COMMENT: 4,
  PROGRAM_FINDER_SHORTLISTED_PROGRAM: 5,
  SCHOLARSHIP_SHORTLISTED: 6,
};

export const VOTE_RELATION_ENTITY_TYPE = {
  QUEST: 1,
  QUEST_ANSWER: 2,
  ANSWER_COMMENT: 3,
};

export const POST_TYPE_QUEST = "QUEST";
export const POST_TYPE_JOIN_COMMUNITIES = "JOIN_COMMUNITIES";
export const POST_TYPE_RELATED_SKYLIFTS = "RELATED_SKYLIFTS";
export const POST_TYPE_QUESTS_YOU_MAY_BACK = "QUESTS_YOU_MAY_BACK";

export const POST_TYPE_MEMBERS_YOU_MAY_CONNECT = "MEMBERS_YOU_MAY_CONNECT";
export const POST_TYPE_SKYLIFT_OFFERED = "SKYLIFT_OFFERED";
export const POST_TYPE_SKYLIFT_BOOKED = "SKYLIFT_BOOKED";
export const POST_TYPE_SKYLIFT_IM_INTERESTED = "SKYLIFT_IM_INTERESTED";

export const POST_SUB_TYPE_QUEST_ANSWERED = "QUEST_ANSWERED";
export const POST_SUB_TYPE_QUEST_STARTED = "QUEST_STARTED";
export const POST_SUB_TYPE_QUEST_IM_BACKED = "QUEST_IM_BACKED";

export const POST_TYPE_TEXT = {
  QUEST_ANSWERED: "answered this Quest ",
  QUEST_STARTED: "started this Quest ",
  QUEST_IM_BACKED: "followed this Quest ",
  SKYLIFT_BOOKED: "booked this ",
  SKYLIFT_OFFERED: "offered this ",
  SKYLIFT_IM_INTERESTED: "marked ",
  JOIN_COMMUNITIES: "joined this Community ",
};

export const TOPIC_ENTITY_RELATION_TYPE = {
  QUEST: 1,
  SKYLIFT: 2,
  GAMEPLAYER: 3,
  SKYLIFT_TEMPLATE: 4,
  WEB_CLIP: 5,
  COMMUNITY: 6,
  KNOWS_ABOUT: 7,
};

export const SHARE_MESSAGE_TEMPLATE = {
  BOOK_SKYLIFT: "book_skylift",
  BOOK_SKYLIFT_TEMPLATE: "book_skylift_template",
};

export const CONVERSATION_PURPOSE_KEY = {
  DEFAULT: "NBYULA_SUPPORT",
  QUEST: "NBYULA_SUPPORT_FOR_QUEST",
  EMAIL: "EMAIL",
};

export const CONTENT_GUIDELINES_URL =
  "https://nbyula.com/q/what-makes-an-answer-to-a-nbyula-quest-useful-and-insightful-to-its-readers#a/6257ca9c38d15e0013169c18";

export const LINK_TEMPLATE = {
  CONTENT_GUIDELINE: "CONTENT_GUIDELINE",
};

export const SKYLIFT_TEMPLATE_FAQ_QUEST =
  "https://nbyula.com/quest/628dffa8ece281001abe1525";

export const UNIT_MEGABYTE = 1048576;
export const VIDEO_UPLOAD_LIMIT_IN_MEGABYTES = 200;

export const PAYMENT_ORDER_STATUS = {
  CREATED: "CREATED",
  CAPTURED: "CAPTURED",
  PAID: "PAID",
  REFUND_INITIATED: "REFUND_INITIATED",
  REFUNDED: "REFUNDED",
  REFUND_REJECTED: "REFUND_REJECTED",
};

export const MESSAGE_EDITOR_ID = "message__editor";
export const COMMENT_EDITOR_ID = "comment-quill-wrapper";

export const AUTH_PROVIDER = {
  CUSTOM: "custom",
  GOOGLE: "google",
  LINKEDIN: "linkedin",
  WHATSAPP: "whatsapp",
  SMS: "sms",
};

export const VARIANTS = {
  DEFAULT: "DEFAULT",
  LARGE: "LARGE",
  SMALL: "SMALL",
  COMPACT: "COMPACT",
};

export const PROFILE_CREDENTIALS = [
  {
    label: "About",
    value: "about",
  },
  {
    label: "Location",
    value: "location",
  },
  {
    label: "Work Experience",
    value: "work",
  },
  {
    label: "Program",
    value: "programs",
  },
  {
    label: "Awards",
    value: "award",
  },
  {
    label: "Scholarship",
    value: "scholarship",
  },
  {
    label: "Certificates",
    value: "certificate",
  },
  {
    label: "Test Scores",
    value: "test-score",
  },
];

export const NBYULA_ORDERS_EMAIL = "orders@nbyula.com";
export const NBYULA_MODERATOR_EMAIL = "moderator@nbyula.com";
export const NBYULA_SUPPORT_EMAIL = "support@nbyula.com";

export const NBYULA_ORDERS_EMAIL_TEMPLATES = {
  ORDER_ACCEPTED: "ORDER_ACCEPTED",
};

export const OFFERED_SKYLIFT_LIMIT = 8;

export const OFFERED_SKYLIFT_COUNT_LIMIT = 6;

export const BOOKMARK_TYPES = {
  LISTING: 1,
  MENTOR: 2,
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const DEFAULT_SPACE_PARTICIPANTS = [
  "6231da961109b3001332a997", // support
  "614891db9055cd001ba8cf3c", // shivansh
];

export const SUPPORT_ASSISTANT_ID = "6231da961109b3001332a997";
export const SUPPORT_ASSISTANT_NAME = "Anika Goel";
export const ANISHA_GAMEPLAYER_ID = "69933354e3aedb00149b50c1";

// Maya, the AI counsellor (prod: maya.kapoor@nbyula.com).
export const MAYA_GAMEPLAYER_ID = "69de34896b24a2006c5a8cae";

// Pure AI personas whose every message is machine-generated. Rakshit is excluded:
// a real person shares that handle, so his messages are told apart by isSystemGenerated.
export const SYSTEM_SENDER_GAMEPLAYER_IDS = [
  SUPPORT_ASSISTANT_ID,
  MAYA_GAMEPLAYER_ID,
];

export const USER_ROLE_MAPPER = {
  ADMIN: "ADMIN",
  MODERATOR: "MODERATOR",
  SERVICE_PROVIDER: "SERVICE_PROVIDER",
};

export const PLAN_TYPES = {
  FREE: "FREE",
  PRO: "PRO",
  PREMIUM: "PREMIUM",
  ULTIMATE: "ULTIMATE",
};

/**
 * LEGACY plan ids (pre-Oct-2026 catalogue: PRO 1, PREMIUM 2, ULTIMATE 3).
 * Catalogue v2 (docs/marketplace/pricing-catalogue-v2.md §2.1) gives planIds
 * NO ordering — v2 rows are 5–16 across four tracks. Use these only to
 * recognise a legacy row or `FREE` (planId 0 = not a paying user); never
 * compare two planIds with < / >. Decide by (track, tier) via
 * pricing/planViewState.js and marketplace/duck/planCart.js; style by tier
 * label (plan.planName / plan.name).
 */
export const PLAN_TYPE_IDS = {
  FREE: 0,
  PRO: 1,
  PREMIUM: 2,
  ULTIMATE: 3,
};

// Buying dMAT hands back to the dMAT workspace, not the generic order page.
// dmat-guide is excluded: it is the free teaser, never purchased.
export const DMAT_SERVICE_SLUGS = [
  "dmat-advanced",
  "dmat-sprint-one",
  "dmat-sprint-two",
  "dmat-bundle",
];

export const DMAT_APP_PATH = "/s/dmat-prep/app";

// The charged webhook can lag, so checkout polls confirm for up to ~20s.
export const SUBSCRIPTION_CONFIRM_ATTEMPTS = 10;
export const SUBSCRIPTION_CONFIRM_INTERVAL_MS = 2000;
